import { useState } from "react";

import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { AsyncButton } from "./ui/async-button.js";
import { Textarea } from "./ui/textarea.js";
import { Badge } from "./ui/badge.js";

const IST = "Asia/Kolkata";

const METRIC_LABEL: Record<string, string> = {
  OVERTIME_APPROVAL: "Overtime",
  WEEKEND_WORK: "Weekend working",
  HOLIDAY_WORK: "Holiday working",
  LEAVE_WORK: "Leave-day working",
  INSUFFICIENT_HOURS: "Insufficient hours",
};

export interface AttendanceApprovalRequest {
  id: string;
  metric: string;
  detail: string;
  date: string;
  hoursWorked: string | number;
  thresholdHours: string | number;
  status: string;
  approverRemark?: string | null;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string };
}

interface Props {
  requests: AttendanceApprovalRequest[];
  onRefresh: () => void;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", { timeZone: IST, day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Attendance exceptions awaiting a manager's decision -- overtime, weekend working,
 * a short day -- in the member detail panel beside the attendance-correction and
 * weekly-hours queues. One queue for every feature, labelled per row, so a manager
 * has a single place to look.
 *
 * Same contract as those: the parent owns the data and passes `onRefresh`; this
 * component only acts. A remark is optional to approve and required to decline, and
 * both handlers rethrow so AsyncButton can end its own pending state correctly.
 */
export function AttendanceApprovalQueue({ requests, onRefresh }: Props) {
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [err, setErr] = useState("");

  async function doApprove(id: string) {
    const remark = (remarks[id] ?? "").trim();
    try {
      await api.patch(`/attendance/approvals/${id}/approve`, remark ? { approverRemark: remark } : {});
      setErr("");
      onRefresh();
    } catch (e) {
      logError("AttendanceApprovalQueue.doApprove", e);
      setErr(errMsg(e) ?? "Could not approve this");
      throw e;
    }
  }

  async function doReject(id: string) {
    const remark = (remarks[id] ?? "").trim();
    // Checked before the request goes out, so the server's 400 is never the way a
    // manager finds out a reason is required.
    if (!remark) {
      setErr("Add a reason before declining");
      throw new Error("validation");
    }
    try {
      await api.patch(`/attendance/approvals/${id}/reject`, { approverRemark: remark });
      setErr("");
      onRefresh();
    } catch (e) {
      logError("AttendanceApprovalQueue.doReject", e);
      setErr(errMsg(e) ?? "Could not decline this");
      throw e;
    }
  }

  if (requests.length === 0) {
    return <div className="p-6 text-center text-[13px] text-surface-500">Nothing awaiting approval.</div>;
  }

  return (
    <div className="flex flex-col gap-2">
      {err && (
        <div className="rounded-md bg-[var(--color-error-bg)] px-3 py-2 text-[13px] text-[var(--color-error-fg)]">{err}</div>
      )}
      {requests.map((r) => (
        <div key={r.id} className="rounded-lg border border-surface-200 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-semibold">
              {r.employee.firstName} {r.employee.lastName}
            </span>
            <span className="text-[11px] text-surface-500">{r.employee.employeeCode}</span>
            <Badge tone="warning">{METRIC_LABEL[r.metric] ?? "Attendance"}</Badge>
            {/* The stored detail already says what happened, so one queue reads
                correctly whichever feature raised the row. */}
            <span className="text-[12px] text-surface-500">{r.detail}</span>
            <span className="ml-auto text-[12px] text-surface-600">{fmtDate(r.date)}</span>
          </div>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <Textarea
              value={remarks[r.id] ?? ""}
              onChange={(e) => setRemarks({ ...remarks, [r.id]: e.target.value })}
              placeholder="Remark (required to decline)"
              rows={1}
              className="min-h-9 flex-1"
            />
            <AsyncButton size="sm" variant="success" onClick={() => doApprove(r.id)}>Approve</AsyncButton>
            <AsyncButton size="sm" variant="destructive" onClick={() => doReject(r.id)}>Decline</AsyncButton>
          </div>
        </div>
      ))}
    </div>
  );
}
